import React, { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { Building2, LifeBuoy, LogOut, Menu, X } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";
import { NotificationsBell } from "../NotificationsBell";
import { useRoleModules } from "../../lib/roleModules";
import { AssistantWidget } from "../assistant/AssistantWidget";   // Phase 171

export const CitizenLayout = ({ code, children }) => {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { allowed, tenantModule, tenantModulesLoaded } = useRoleModules(code);

  const items = [
    { to: `/${code}/portal`, label: "Home", end: true, testId: "citizen-nav-home", module: "home" },
    { to: `/${code}/portal/properties`, label: "My Properties", testId: "citizen-nav-properties", module: "properties" },
    { to: `/${code}/portal/self-assessment`, label: "Self Assessment", testId: "citizen-nav-self-assessment", module: "self_assessment" },
    { to: `/${code}/portal/services`, label: "Services", testId: "citizen-nav-services", module: "services" },
    { to: `/${code}/portal/parking`, label: "Parking", testId: "citizen-nav-parking", module: "parking", tenant: "smart_parking" },   // Phase 201
    { to: `/${code}/portal/grievances`, label: "Grievances", testId: "citizen-nav-grievances", module: "grievances" },
    { to: `/${code}/portal/alerts`, label: "My Alerts", testId: "citizen-nav-alerts", module: "alerts" },
  ].filter((it) => allowed(it.module) && (!it.tenant || (tenantModulesLoaded && tenantModule(it.tenant))));

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="sticky top-0 z-40 bg-card border-b">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to={`/${code}/portal`} className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center shrink-0">
              <Building2 className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-sm font-heading truncate">{auth?.municipality?.name || "Citizen Portal"}</p>
              <p className="text-xs text-muted-foreground">Citizen Portal</p>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testId}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-medium ${isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground"}`
                }>
                {it.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1">
            <NotificationsBell code={code} />
            <Button variant="ghost" size="icon" onClick={() => navigate(`/${code}/portal/help`)} aria-label="Help" title="Help — how to use the portal" data-testid="citizen-nav-help">
              <LifeBuoy className="h-5 w-5" />
            </Button>
            <span className="text-sm font-medium hidden sm:block">{auth?.user?.name}</span>
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/"); }} data-testid="citizen-logout-button">
              <LogOut className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(!open)}>
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
        {open && (
          <nav className="md:hidden border-t px-4 py-2 flex flex-col gap-1 bg-card">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-lg text-sm font-medium ${isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground"}`
                }>
                {it.label}
              </NavLink>
            ))}
          </nav>
        )}
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6">{children}</main>
      <AssistantWidget code={code} />
      <footer className="border-t py-4 text-center text-xs text-muted-foreground">
        MunicipalConnect — {auth?.municipality?.name} • Powered by National Municipal Services Platform
      </footer>
    </div>
  );
};
